import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Exact, sourcePath, type ItemMovementDto, type ItemMovementRow } from "@delta/shared";
import type {
  AccountMovementsDto, BalanceSheetDto, FsSection, IncomeStatementDto, InvoiceDetailRow, InvoiceDetailsDto, InvoiceProfitRow, InvoiceProfitsDto, InvoiceReportDto, InvoiceReportRow,
  MoneyKind, MoneyReportDto, MoneyReportRow, MovementSummaryDto, MovementSummaryRow, ReportInvoiceKind, ReportOptionsDto, TaxReturnDto, TaxReturnRow, TobaccoReportDto, TobaccoReportRow,
  TransferReportDto, TransferReportRow, WarehouseBalanceRow, WarehouseBalancesDto, WarehouseItemRow, WarehouseItemsDto,
  AssetInvoiceDetailRow, AssetInvoiceDetailsDto, AssetInvoiceReportDto, AssetInvoiceReportRow, AssetOptionsDto, AssetReportDto, AssetReportRow, DepreciationRowDto, DepreciationsDto
} from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { api } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { AccName, groupPairs } from "../accounting/common";
import { treeRows } from "./tree-rows";
import { Body, Frame, useReport } from "./Reports";
import { amt, Amt, initialPeriod, isZero, PeriodChip, periodQuery, ReportFilters, ReportTable, useLedgerOptions, usePrintData, when, type Filter, type Period, type RCol } from "./common";
import { buildXlsx } from "../../lib/sheet";
import { loadPrintFonts, loadPrintImage, renderDoc } from "../../print/render";
import { rastersToPdf, saveBlob } from "../../print/pdf";
import { reportDoc } from "../../print/report";
import { partyName } from "../../lib/party";
import { useSession } from "../../lib/session";
import { indentPx } from "../../lib/indent";

// Phase 9 reports, ported from the prototype (erp-reports.js): income statement, balance sheet, invoice registers,
// invoice details and profits, receipts and payments by kind, cash transfers, movement summary, sales and purchase tax,
// the tax return and the warehouse reports; and the fixed asset reports of phase 10 (rule T-8, section 11). Every figure comes from the server. Rules F-1 to F-6 are provisional
// (docs/proposed-financial-rules.md, section 9). A reversed document is listed again, negated, on its reversal date (F-2).

/** Branches, warehouses, treasuries, customers and suppliers, users, items and categories for the filters, in the caller's scope. */
function useReportOptions(): ReportOptionsDto | null {
  const [o, setO] = useState<ReportOptionsDto | null>(null);
  useEffect(() => { api<ReportOptionsDto>("GET", "/reports/options").then(setO, () => setO(null)); }, []);
  return o;
}

/** "sale_refund" → the prototype's label key "src_SaleRefund". */
export const srcLabel = (t: T, k: string) => t("src_" + (k === "stock_transfer" ? "Transfer" : k === "opening_balance" ? "OpeningBalance" : k.replace(/(^|_)([a-z])/g, (_, __, c: string) => c.toUpperCase())));
const neg = (x: string) => x.startsWith("-") && !isZero(x);
const Signed = ({ v }: { v: string }) => <span className={`num ${neg(v) ? "neg" : ""}`}>{amt(v)}</span>;
const qs = (url: string, p: Period | null, pairs: [string, string | undefined][]) => {
  const q = [p ? periodQuery(p) : "", ...pairs.filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v!)}`)].filter(Boolean).join("&");
  return url + (q ? "?" + q : "");
};
const Rev = () => { const { t } = useLang(); return <> <span className="pill" data-testid="rp-reversal">{t("rpReversal")}</span></>; };
/** Document number linked to its page, with the reversal pill (F-2). */
const DocNo = ({ to, code, reversal }: { to: string; code: string | null; reversal: boolean }) => <><Link className="num" to={to}>{code}</Link>{reversal && <Rev />}</>;
const docText = (t: T, code: string | null, reversal: boolean) => `${code ?? ""}${reversal ? " " + t("rpReversal") : ""}`;
const Provisional = () => { const { t } = useLang(); return <div className="dep-note" data-testid="rp-provisional"><Icon name="info" /><span>{t("fsProvisional")}</span></div>; };
const total = (t: T): [ReactNode, string] => [t("total"), t("total")];
const blank: [ReactNode, string] = ["", ""];
const fcell = (x: string): [ReactNode, string] => [amt(x), amt(x)];

/** Options for the party, warehouse, user, item and branch filters. */
function useFilterOpts() {
  const { t, L } = useLang();
  const o = useReportOptions();
  return {
    o,
    wh: (o?.warehouses ?? []).map((w) => [w.id, L(w)] as [string, string]),
    br: (o?.branches ?? []).map((b) => [b.id, L(b)] as [string, string]),
    tr: (o?.treasuries ?? []).map((x) => [x.id, L(x)] as [string, string]),
    party: (kind?: "customer" | "supplier") => (o?.parties ?? []).filter((x) => !kind || x.kind === kind).map((x) => [x.id, `${x.name} · ${t(x.kind)}`] as [string, string]),
    users: (o?.users ?? []).map((u) => [u.id, u.name] as [string, string]),
    items: (o?.items ?? []).map((x) => [x.id, `${x.code} ${L(x)}`] as [string, string]),
    cats: (o?.categories ?? []).map((x) => [x.id, L(x)] as [string, string])
  };
}

/** Period, filters and the first load with the defaults. */
function useLoaded<D>(build: (p: Period | null, v: Record<string, string>) => string | null, preset: string | null = "thisYear", initial: Record<string, string> = {}) {
  const [p, setP] = useState<Period | null>(() => (preset ? initialPeriod(preset) : null));
  const [v, setV] = useState<Record<string, string>>(initial);
  const r = useReport<D>(build);
  useEffect(() => { void r.run(p, v); }, [build]);
  const search = (pp: Period | null, vv: Record<string, string>) => { setP(pp); setV(vv); void r.run(pp, vv); };
  return { p, v, r, search };
}

// ---------- financial statements (F-1, F-3) ----------

type FsLine = { head: string } | { sub: string; amount: string; testId?: string } | { grand: string; amount: string; testId?: string } | { row: FsSection["rows"][number]; depth?: number } | { calc: string; amount: string }
  | { grp: FsSection["groups"][number]; depth: number };
/** A section's accounts; with `byGroup`, in chart order under their groups' subtotals (each account counted once, rule H-6). */
function sectionLines(s: FsSection, byGroup: boolean): FsLine[] {
  return byGroup ? treeRows(s.groups, s.rows, (r) => r.typeId).map((x) => ("g" in x ? { grp: x.g, depth: x.depth } : { row: x.r, depth: x.depth })) : s.rows.map((row) => ({ row }));
}
function fsRows(L: (x: { nameEn: string; nameAr: string }) => string, t: T, s: FsSection, subLabel: string, extra?: FsLine, byGroup = false): FsLine[] {
  if (!s.rows.length && !extra) return [];
  return [{ head: L(s) }, ...sectionLines(s, byGroup), ...(extra ? [extra] : []), { sub: subLabel, amount: s.total }];
}
const fsInd = (x: FsLine) => ("depth" in x && x.depth ? { paddingInlineStart: `${indentPx(x.depth - 1, 16, 18)}px` } : undefined);
function fsCols(t: T, L: (x: { nameEn: string; nameAr: string }) => string, valueLabel: string): RCol<FsLine>[] {
  return [
    { label: t("name"), cell: (x) => ("row" in x ? <span className="rp-ind2" style={fsInd(x)}><AccName a={x.row.account} /></span> : "grp" in x ? <span className="rp-ind2" style={fsInd(x)} data-testid="fs-group"><b>{x.grp.group.code && <span className="num muted">{x.grp.group.code} </span>}{L(x.grp.group)}</b></span>
      : "calc" in x ? <span className="rp-ind2"><i>{x.calc}</i></span> : <b>{"head" in x ? x.head : "sub" in x ? x.sub : x.grand}</b>),
      text: (x) => ("row" in x ? `${"  ".repeat(x.depth ?? 1)}${x.row.account.code} ${L(x.row.account)}` : "grp" in x ? `${"  ".repeat(x.depth)}${x.grp.group.code ?? ""} ${L(x.grp.group)}`.trimEnd() : "head" in x ? x.head : "sub" in x ? x.sub : "calc" in x ? x.calc : x.grand) },
    { label: valueLabel, end: true, cell: (x) => ("head" in x ? "" : "row" in x ? <Signed v={x.row.amount} /> : "grp" in x ? <b><Signed v={x.grp.amount} /></b> : <b data-testid={"grand" in x || "sub" in x ? x.testId : undefined}><Signed v={x.amount} /></b>),
      text: (x) => ("head" in x ? "" : amt("row" in x ? x.row.amount : "grp" in x ? x.grp.amount : x.amount)) }
  ];
}
const fsCls = (x: FsLine) => ("head" in x ? "rp-h" : "sub" in x || "grp" in x ? "rp-s" : "grand" in x ? "rp-g" : "");

export function IncomeStatementReport() {
  const { t, L } = useLang();
  const { o } = useFilterOpts();
  const types = [5, 4].flatMap((c) => groupPairs(L, o?.types ?? [], c));
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/income-statement", p, [["typeId", v.type]]), []);
  const { p, v, r, search } = useLoaded<IncomeStatementDto>(build);
  const d = r.data;
  const byGroup = v.view === "groups";
  const rows: FsLine[] = d ? [...fsRows(L, t, d.revenues, t("totalRevenues"), undefined, byGroup), ...fsRows(L, t, d.expenses, t("totalExpenses"), undefined, byGroup)] : [];
  if (d && rows.length) rows.push({ grand: t("netProfit"), amount: d.netProfit, testId: "is-net" });
  const title = t("incomeStatement");
  return (
    <Frame group="repFinancial" title={title} filters={<><Provisional /><ReportFilters period={p} filters={[{ k: "type", label: t("type"), options: types, wide: true }, { k: "view", label: t("tbView"), options: [["groups", t("tbViewGroups")]] }]} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="income-statement" cls="rp-fs" cols={fsCols(t, L, t("netMovement"))} rows={rows} rowCls={fsCls}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

export function BalanceSheetReport() {
  const { t, L } = useLang();
  const { branches } = useSession();
  // The period's end is the balance sheet's date; its start splits the result into earlier periods' and the current one's.
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/balance-sheet", null, [["from", p?.from || undefined], ["to", p?.to || undefined], ["branchId", v.branchId]]), []);
  const { p, v, r, search } = useLoaded<BalanceSheetDto>(build);
  const d = r.data;
  // Equity: its accounts, then the results not yet closed into it, each shown once (as one line, or split at the
  // period's start into earlier periods' and this period's), and the equity total that includes them (D-37).
  const results: FsLine[] = !d ? [] : d.periodFrom
    ? [{ calc: `${t("bsPrior")} (< ${d.periodFrom})`, amount: d.priorResult }, { calc: `${t("bsCurrent")} (${d.periodFrom} – ${d.asAt ?? "…"})`, amount: d.currentResult }]
    : [{ calc: t("profitNotClosed"), amount: d.profit }];
  const byGroup = v.view === "groups";
  const equity: FsLine[] = d ? [{ head: L(d.equity) }, ...sectionLines(d.equity, byGroup), ...results, { sub: `${t("total")} ${L(d.equity)}`, amount: d.totalEquity, testId: "bs-equity" }] : [];
  const rows: FsLine[] = d ? [...fsRows(L, t, d.assets, `${t("total")} ${L(d.assets)}`, undefined, byGroup), ...fsRows(L, t, d.liabilities, `${t("total")} ${L(d.liabilities)}`, undefined, byGroup), ...equity] : [];
  if (d) rows.push({ grand: t("totalAssets"), amount: d.totalAssets, testId: "bs-assets" }, { grand: t("totalLiabEq"), amount: d.totalLiabilitiesEquity, testId: "bs-le" });
  const ok = d && isZero(d.difference);
  const title = t("balanceSheet");
  const notes = d ? [ok ? t("bsBalanced") : t("bsNotBalanced"),
    t("bsEquation", { a: amt(d.totalAssets), l: amt(d.totalLiabilities), e: amt(d.totalEquity), r: amt(d.profit), d: amt(d.difference) }),
    ...(d.branch && d.unbranched ? [t("stUnbranched", { n: d.unbranched })] : [])] : [];
  const cols = fsCols(t, L, t("balance"));
  // Each account opens its statement up to the balance sheet's date.
  const name = cols[0]!;
  cols[0] = { ...name, cell: (x) => ("row" in x ? <span className="rp-ind2" style={fsInd(x)}><Link to={`/reports/charts-statement?accountId=${x.row.account.id}`} data-testid="bs-acc"><AccName a={x.row.account} /></Link></span> : name.cell(x)) };
  return (
    <Frame group="repFinancial" title={title} filters={<><Provisional /><ReportFilters period={p} filters={[{ k: "branchId", label: t("rpBranch"), options: branches.map((b) => [b.id, L(b)] as [string, string]) }, { k: "view", label: t("tbView"), options: [["groups", t("tbViewGroups")]] }]} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <>
        <div className={`banner ${ok ? "good" : "bad"} rp-check`} data-testid="bs-check"><Icon name={ok ? "check" : "info"} /><span>{notes[0]} {notes[1]}</span></div>
        {d.branch && d.unbranched > 0 && <div className="banner rp-check" data-testid="bs-unbranched"><Icon name="info" /><span>{notes[2]}</span></div>}
        <ReportTable title={title} file="balance-sheet" cls="rp-fs" cols={cols} rows={rows} rowCls={fsCls} notes={notes}
          chips={<><span className="fchip">{t("rpAsAt")}: <b className="num">{d.asAt ?? t("pr_all")}</b></span>{d.periodFrom && <span className="fchip">{t("bsPeriodFrom")}: <b className="num">{d.periodFrom}</b></span>}
            {d.branch && <span className="fchip">{t("rpBranch")}: <b>{L(d.branch)}</b></span>}</>} testId="report-table" />
      </>}</Body>
    </Frame>
  );
}

// ---------- tax (F-4) ----------

const SRC_VAT = { commissions: "rpCommissionVat", assets: "rpAssetVat", expenses: "rpExpenseVat" } as const;
export function TaxReturnReport() {
  const { t, L } = useLang();
  const build = useCallback((p: Period | null) => qs("/reports/tax-return", p, []), []);
  const { p, v, r, search } = useLoaded<TaxReturnDto>(build);
  const d = r.data;
  const cols: RCol<TaxReturnRow>[] = [
    { label: t("name"), cell: (x) => <>{L(x.taxType)}{x.source !== "invoices" && <span className="muted"> · {t(SRC_VAT[x.source])}</span>}</>, text: (x) => L(x.taxType) + (x.source !== "invoices" ? ` · ${t(SRC_VAT[x.source])}` : "") },
    { label: t("amount"), end: true, cell: (x) => <Signed v={x.taxable} />, text: (x) => amt(x.taxable) },
    { label: t("percentagePct"), end: true, cell: (x) => <span className="num">{x.rate}</span>, text: (x) => x.rate },
    { label: t("tax"), end: true, cell: (x) => <Signed v={x.tax} />, text: (x) => amt(x.tax) }
  ];
  const title = t("taxReturn");
  const due = d && !neg(d.net);
  return (
    <Frame group="repTaxes" title={title} filters={<><Provisional /><ReportFilters period={p} filters={[]} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <>
        <div className="rp-net" data-testid="tax-net">
          <div><span>{t("salesTax")}</span><b className="num">{amt(d.output.tax)}</b></div><span className="op">−</span>
          <div><span>{t("purchaseTax")}</span><b className="num">{amt(d.input.tax)}</b></div><span className="op">=</span>
          <div className="tot"><span>{t("net")}</span><b className="num" data-testid="tax-net-value">{amt(d.net)}</b><small>{t(due ? "taxDue" : "taxRefundable")}</small></div>
        </div>
        <div className={`banner ${isZero(d.difference) ? "good" : "bad"} rp-check`} data-testid="tax-ledger"><Icon name={isZero(d.difference) ? "check" : "info"} />
          <span>{t("rpTaxLedger", { x: amt(d.ledger) })} {isZero(d.difference) ? t("rpTaxLedgerOk") : t("rpTaxLedgerDiff", { x: amt(d.difference) })}</span></div>
        <ReportTable title={t("salesTax")} file="tax-return-output" cols={cols} rows={d.output.rows} foot={[total(t), fcell(d.output.taxable), blank, fcell(d.output.tax)]}
          chips={<PeriodChip from={d.from} to={d.to} />} testId="tax-output" />
        <ReportTable title={t("purchaseTax")} file="tax-return-input" cols={cols} rows={d.input.rows} foot={[total(t), fcell(d.input.taxable), blank, fcell(d.input.tax)]} testId="tax-input" />
      </>}</Body>
    </Frame>
  );
}

// ---------- tobacco municipality fees (migration 049) ----------

/**
 * Fees collected on posted sales, returned by refunds, and the difference, by date and branch, with each invoice
 * linked and the liability account's balance. The amounts are the ones stored on the invoices, so the report keeps
 * working, unchanged, after the fee is switched off. Excel (.xlsx) and PDF downloads use the same rows.
 */
export function TobaccoFeesReport() {
  const { t, L, lang } = useLang();
  const f = useFilterOpts();
  const printData = usePrintData();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/tobacco-fees", p, [["branchId", v.br]]), []);
  const { p, v, r, search } = useLoaded<TobaccoReportDto>(build);
  const d = r.data;
  const [busy, setBusy] = useState(false);
  const kindText = (x: TobaccoReportRow) => t(x.kind === "sale" ? "tobKindSale" : "tobKindRefund");
  const cols: RCol<TobaccoReportRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("invoiceNo"), cell: (x) => <DocNo to={`/invoices/show/${x.invoiceId}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("kind"), cell: kindText, text: kindText },
    { label: t("branch"), cell: (x) => L(x.branch), text: (x) => L(x.branch) },
    { label: t("customerName"), cell: (x) => x.party ?? <span className="muted">{t("walkInCustomer")}</span>, text: (x) => x.party ?? t("walkInCustomer") },
    { label: t("tobBase"), end: true, cell: (x) => <Signed v={x.base} />, text: (x) => amt(x.base) },
    { label: t("tobFee"), end: true, cell: (x) => <Signed v={x.fee} />, text: (x) => amt(x.fee) }
  ];
  const title = t("tobReport");
  const header = cols.map((c) => c.label);
  const sheet = (x: TobaccoReportDto) => [[title], header, ...x.rows.map((row) => cols.map((c) => c.text(row))),
    [], [t("tobCollected"), x.collected], [t("tobRefunded"), x.refunded], [t("tobNet"), x.net], ...x.accounts.map((a) => [t("tobBalanceOf", { a: `${a.account.code} ${L(a.account)}` }), a.balance])];
  function xlsx(x: TobaccoReportDto) { saveBlob(buildXlsx(t("tobReportSheet"), sheet(x), [1]), `tobacco-fees-${x.from ?? "all"}-${x.to ?? "all"}.xlsx`); }
  const data = (x: TobaccoReportDto) => printData(title, cols.map((c) => ({ label: c.label, end: c.end })), x.rows.map((row) => ({ cells: cols.map((c) => c.text(row)) })),
    [t("total"), "", "", "", "", "", amt(x.net)],
    [`${t("tobCollected")}: ${amt(x.collected)}`, `${t("tobRefunded")}: ${amt(x.refunded)}`, `${t("tobNet")}: ${amt(x.net)}`, ...x.accounts.map((a) => t("tobBalanceOf", { a: `${a.account.code} ${L(a.account)}` }) + `: ${amt(a.balance)}`)]);
  async function pdf(x: TobaccoReportDto) {
    setBusy(true);
    try {
      const doc = data(x);
      const [logo] = await Promise.all([loadPrintImage("/api/organization/logo"), loadPrintFonts()]);
      const pages = renderDoc(reportDoc(doc, logo), { widthMm: 210, heightMm: 297, dpi: 150, marginMm: 12 });
      saveBlob(await rastersToPdf(pages, 150, title), `tobacco-fees-${x.from ?? "all"}-${x.to ?? "all"}.pdf`);
    } finally { setBusy(false); }
  }
  return (
    <Frame group="repTaxes" title={title} filters={<ReportFilters period={p} filters={[{ k: "br", label: t("branch"), options: f.br }]} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{d && <>
        <div className="rp-net" data-testid="tob-net" dir={lang === "ar" ? "rtl" : "ltr"}>
          <div><span>{t("tobCollected")}</span><b className="num" data-testid="tob-collected">{amt(d.collected)}</b></div><span className="op">−</span>
          <div><span>{t("tobRefunded")}</span><b className="num" data-testid="tob-refunded">{amt(d.refunded)}</b></div><span className="op">=</span>
          <div className="tot"><span>{t("tobNet")}</span><b className="num" data-testid="tob-netv">{amt(d.net)}</b></div>
        </div>
        {d.accounts.map((a) => <div key={a.account.id} className="banner rp-check" data-testid="tob-balance"><Icon name="info" />
          <span>{t("tobBalanceOf", { a: `${a.account.code} ${L(a.account)}` })}: <b className="num">{amt(a.balance)}</b> · {t("tobBalanceNote")}</span></div>)}
        <div className="rp-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0" }}>
          <button className="btn btn-sm" onClick={() => xlsx(d)} disabled={!d.rows.length} data-testid="tob-xlsx"><Icon name="file" />{t("tobXlsx")}</button>
          <button className="btn btn-sm" onClick={() => void pdf(d)} disabled={!d.rows.length || busy} data-testid="tob-pdf"><Icon name="download" />{t("tobPdf")}</button>
        </div>
        <ReportTable title={title} file="tobacco-fees" cols={cols} rows={d.rows} foot={[total(t), blank, blank, blank, blank, blank, fcell(d.net)]}
          chips={<PeriodChip from={d.from} to={d.to} />} testId="tob-table" />
      </>}</Body>
    </Frame>
  );
}

// ---------- invoice registers and tax reports ----------

const INV_ROUTE: Record<string, ReportInvoiceKind> = { Sale: "sale", Purchase: "purchase", SaleRefund: "sale_refund", PurchaseRefund: "purchase_refund" };
const INV_TITLE: Record<ReportInvoiceKind, string> = { sale: "sales", purchase: "purchases", sale_refund: "salesRefunded", purchase_refund: "purchasesRefund" };

function InvoiceTable({ title, file, d, withPaid }: { title: string; file: string; d: InvoiceReportDto; withPaid: boolean }) {
  const { t, L } = useLang();
  const fees = d.rows.some((x) => !isZero(x.tobaccoFee ?? "0"));
  const cols: RCol<InvoiceReportRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("warehouse"), cell: (x) => L(x.warehouse), text: (x) => L(x.warehouse) },
    { label: t("account"), cell: (x) => partyName(t, x.party), text: (x) => partyName(t, x.party) },
    { label: t("invoiceNo"), cell: (x) => <DocNo to={`/invoices/show/${x.id}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("kind"), cell: (x) => srcLabel(t, x.kind), text: (x) => srcLabel(t, x.kind) },
    { label: t("subTotal"), end: true, cell: (x) => <Signed v={x.subtotal} />, text: (x) => amt(x.subtotal) },
    // Tobacco municipal fees (migration 049), apart from VAT: shown when any row has one, so the columns add up to the total.
    ...(fees ? [{ label: t("tobFee"), end: true, cell: (x: InvoiceReportRow) => <Signed v={x.tobaccoFee} />, text: (x: InvoiceReportRow) => amt(x.tobaccoFee) }] : []),
    { label: t("tax"), end: true, cell: (x) => <Signed v={x.vat} />, text: (x) => amt(x.vat) },
    { label: t("grandTotal"), end: true, cell: (x) => <Signed v={x.grand} />, text: (x) => amt(x.grand) },
    ...(withPaid ? [
      { label: t("paidTotal"), end: true, cell: (x: InvoiceReportRow) => <Signed v={x.paid} />, text: (x: InvoiceReportRow) => amt(x.paid) },
      { label: t("remaining"), end: true, cell: (x: InvoiceReportRow) => <Signed v={x.remaining} />, text: (x: InvoiceReportRow) => amt(x.remaining) }] : []),
    { label: t("by"), cell: (x) => x.byName ?? <span className="muted">–</span>, text: (x) => x.byName ?? "" }
  ];
  const foot: [ReactNode, string][] = [total(t), blank, blank, blank, blank, fcell(d.subtotal), ...(fees ? [fcell(d.tobaccoFee)] : []), fcell(d.vat), fcell(d.grand), ...(withPaid ? [fcell(d.paid), fcell(d.remaining)] : []), blank];
  return <ReportTable title={title} file={file} cols={cols} rows={d.rows} foot={foot} chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />;
}

export function InvoiceRegisterReport() {
  const { kind: route = "Sale" } = useParams();
  const kind = INV_ROUTE[route] ?? "sale";
  const { t } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/invoices", p, [["kind", kind], ["warehouseId", v.wh], ["partyId", v.acc], ["userId", v.by]]), [kind]);
  const { p, v, r, search } = useLoaded<InvoiceReportDto>(build);
  const title = t(INV_TITLE[kind]);
  const filters: Filter[] = [{ k: "wh", label: t("warehouse"), options: f.wh }, { k: "acc", label: t("account"), options: f.party(kind.startsWith("sale") ? "customer" : "supplier"), wide: true }, { k: "by", label: t("by"), options: f.users }];
  return (
    <Frame group="tabInvoices" title={title} filters={<ReportFilters key={kind} period={p} filters={filters} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{r.data && <InvoiceTable title={title} file={`invoices-${route.toLowerCase()}`} d={r.data} withPaid />}</Body>
    </Frame>
  );
}

export function InvoiceTaxReport() {
  const { kind: route = "Sale" } = useParams();
  const side = route === "Purchase" ? "purchase" : "sale";
  const { t } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/tax", p, [["side", side], ["kind", v.kind], ["warehouseId", v.wh], ["partyId", v.acc], ["userId", v.by]]), [side]);
  const { p, v, r, search } = useLoaded<InvoiceReportDto>(build);
  const title = t(side === "sale" ? "salesTax" : "purchaseTax");
  const kinds: ReportInvoiceKind[] = side === "sale" ? ["sale", "sale_refund"] : ["purchase", "purchase_refund"];
  const filters: Filter[] = [{ k: "kind", label: t("kind"), options: kinds.map((k) => [k, srcLabel(t, k)]) }, { k: "wh", label: t("warehouse"), options: f.wh },
    { k: "acc", label: t("account"), options: f.party(side === "sale" ? "customer" : "supplier"), wide: true }, { k: "by", label: t("by"), options: f.users }];
  return (
    <Frame group="repTaxes" title={title} filters={<ReportFilters key={side} period={p} filters={filters} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{r.data && <InvoiceTable title={title} file={side === "sale" ? "sales-tax" : "purchase-tax"} d={r.data} withPaid={false} />}</Body>
    </Frame>
  );
}

const DETAIL_KINDS = ["purchase", "purchase_refund", "sale", "sale_refund", "beginning_inventory", "corrupted"];
export function InvoiceDetailsReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/invoice-details", p, [["kind", v.kind], ["warehouseId", v.wh], ["partyId", v.acc], ["userId", v.by], ["itemId", v.item]]), []);
  const { p, v, r, search } = useLoaded<InvoiceDetailsDto>(build);
  const d = r.data;
  const side = (s: "in" | "out", k: "qty" | "price" | "tax" | "total", label: string): RCol<InvoiceDetailRow> => ({
    label, grp: t(s === "in" ? "rpIn" : "rpOut"), end: true,
    cell: (x) => (x.side !== s ? <span className="muted">–</span> : k === "qty" ? <span className="num">{x.qty.replace(/\.?0+$/, "")}</span> : <Signed v={x[k]} />),
    text: (x) => (x.side !== s ? "" : k === "qty" ? x.qty : amt(x[k]))
  });
  const cols: RCol<InvoiceDetailRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("warehouse"), cell: (x) => L(x.warehouse), text: (x) => L(x.warehouse) },
    { label: t("invoiceNo"), cell: (x) => <DocNo to={`/invoices/show/${x.id}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("kind"), cell: (x) => srcLabel(t, x.kind), text: (x) => srcLabel(t, x.kind) },
    { label: t("itemName"), cell: (x) => <Link to={`/items/show/${x.item.id}`}>{L(x.item)}</Link>, text: (x) => L(x.item) },
    { label: t("unit"), cell: (x) => L(x.unit), text: (x) => L(x.unit) },
    side("in", "qty", t("qty")), side("in", "price", t("price")), side("in", "tax", t("tax")), side("in", "total", t("total")),
    side("out", "qty", t("qty")), side("out", "price", t("price")), side("out", "tax", t("tax")), side("out", "total", t("total")),
    { label: t("by"), cell: (x) => x.byName ?? <span className="muted">–</span>, text: (x) => x.byName ?? "" }
  ];
  const title = t("invoicesDetails");
  const filters: Filter[] = [{ k: "kind", label: t("kind"), options: DETAIL_KINDS.map((k) => [k, srcLabel(t, k)]) }, { k: "wh", label: t("warehouse"), options: f.wh },
    { k: "acc", label: t("account"), options: f.party(), wide: true }, { k: "by", label: t("by"), options: f.users }, { k: "item", label: t("itemName"), options: f.items, wide: true }];
  return (
    <Frame group="tabInvoices" title={title} filters={<ReportFilters period={p} filters={filters} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="invoices-details" cols={cols} rows={d.rows}
        foot={[total(t), blank, blank, blank, blank, blank, blank, blank, fcell(d.inTax), fcell(d.inTotal), blank, blank, fcell(d.outTax), fcell(d.outTotal), blank]}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

export function InvoiceProfitsReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/invoice-profits", p, [["warehouseId", v.wh], ["itemId", v.item]]), []);
  const { p, v, r, search } = useLoaded<InvoiceProfitsDto>(build);
  const d = r.data;
  const cols: RCol<InvoiceProfitRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("branch"), cell: (x) => L(x.branch), text: (x) => L(x.branch) },
    { label: t("warehouse"), cell: (x) => L(x.warehouse), text: (x) => L(x.warehouse) },
    { label: t("invoiceNo"), cell: (x) => <DocNo to={`/invoices/show/${x.id}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("itemName"), cell: (x) => L(x.item), text: (x) => L(x.item) },
    { label: t("unit"), cell: (x) => L(x.unit), text: (x) => L(x.unit) },
    { label: t("qty"), end: true, cell: (x) => <span className="num">{x.qty.replace(/\.?0+$/, "")}</span>, text: (x) => x.qty },
    { label: t("rpNetSales"), end: true, cell: (x) => <Signed v={x.sales} />, text: (x) => amt(x.sales) },
    { label: t("cost"), end: true, cell: (x) => <Signed v={x.cost} />, text: (x) => amt(x.cost) },
    { label: t("profit"), end: true, cell: (x) => <Signed v={x.profit} />, text: (x) => amt(x.profit) }
  ];
  const title = t("invoicesProfits");
  return (
    <Frame group="tabInvoices" title={title} filters={<ReportFilters period={p} filters={[{ k: "wh", label: t("warehouse"), options: f.wh }, { k: "item", label: t("itemName"), options: f.items, wide: true }]} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="invoices-profits" cols={cols} rows={d.rows}
        foot={[total(t), blank, blank, blank, blank, blank, blank, fcell(d.sales), fcell(d.cost), [<span data-testid="profit-total">{amt(d.profit)}</span>, amt(d.profit)]]}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

// ---------- treasury ----------

const MONEY_ROUTE: Record<string, MoneyKind> = { Collect: "collect", Payment: "payment", Deposit: "deposit", Withdraw: "withdraw", Expense: "expense" };
const MONEY_TITLE: Record<MoneyKind, string> = { collect: "collects", payment: "payments", deposit: "deposit", withdraw: "withdraw", expense: "expenses" };

export function MoneyReport({ all }: { all?: boolean }) {
  const { kind: route = "Collect" } = useParams();
  const kind = all ? undefined : MONEY_ROUTE[route] ?? "collect";
  const { t, L } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/money", p, [["kind", kind ?? v.kind], ["branchId", v.br], ["partyId", v.acc], ["userId", v.by]]), [kind]);
  const { p, v, r, search } = useLoaded<MoneyReportDto>(build);
  const d = r.data;
  const cols: RCol<MoneyReportRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("kind"), cell: (x) => <>{srcLabel(t, x.kind)} <DocNo to={`/receipt/${x.id}`} code={x.code} reversal={x.reversal} /></>, text: (x) => `${srcLabel(t, x.kind)} ${docText(t, x.code, x.reversal)}` },
    { label: t("branch"), cell: (x) => (x.branch ? L(x.branch) : "–"), text: (x) => (x.branch ? L(x.branch) : "") },
    { label: t("account"), cell: (x) => (x.party ? x.party.name : x.account ? <AccName a={x.account} /> : "–"), text: (x) => (x.party ? x.party.name : x.account ? `${x.account.code} ${L(x.account)}` : "") },
    { label: t("debit"), end: true, cell: (x) => <Amt v={x.debit} zero />, text: (x) => amt(x.debit) },
    { label: t("credit"), end: true, cell: (x) => <Amt v={x.credit} zero />, text: (x) => amt(x.credit) },
    { label: t("balance"), end: true, cell: (x) => <Signed v={x.balance} />, text: (x) => amt(x.balance) },
    { label: t("note"), cell: (x) => <span className="muted">{x.note}</span>, text: (x) => x.note },
    { label: t("by"), cell: (x) => x.byName ?? <span className="muted">–</span>, text: (x) => x.byName ?? "" }
  ];
  const title = t(kind ? MONEY_TITLE[kind] : "receiptPayment");
  const filters: Filter[] = [...(kind ? [] : [{ k: "kind", label: t("kind"), options: Object.keys(MONEY_TITLE).map((k) => [k, srcLabel(t, k)] as [string, string]) }]),
    { k: "br", label: t("branch"), options: f.br }, { k: "acc", label: t("account"), options: f.party(kind === "collect" ? "customer" : kind === "payment" ? "supplier" : undefined), wide: true }, { k: "by", label: t("by"), options: f.users }];
  return (
    <Frame group={kind === "expense" ? "treasury" : kind ? "clients" : "repAccounting"} title={title} filters={<ReportFilters key={route} period={p} filters={filters} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file={kind ? `receipts-${kind}` : "receipts-and-payments"} cols={cols} rows={d.rows}
        foot={[total(t), blank, blank, blank, fcell(d.debit), fcell(d.credit), [<span data-testid="money-balance">{amt(d.balance)}</span>, amt(d.balance)], blank, blank]}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

export function TransfersReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/transfers", p, [["fromTreasuryId", v.fromTr], ["toTreasuryId", v.toTr], ["userId", v.by]]), []);
  const { p, v, r, search } = useLoaded<TransferReportDto>(build);
  const d = r.data;
  const cols: RCol<TransferReportRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("rpDocNo"), cell: (x) => <DocNo to={`/transfer/${x.id}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("treasury"), cell: (x) => L(x.fromTreasury), text: (x) => L(x.fromTreasury) },
    { label: t("toTreasury"), cell: (x) => L(x.toTreasury), text: (x) => L(x.toTreasury) },
    { label: t("amount"), end: true, cell: (x) => <Signed v={x.amount} />, text: (x) => amt(x.amount) },
    { label: t("depositNumber"), cell: (x) => (x.depositNumber ? <span className="num">{x.depositNumber}</span> : <span className="muted">–</span>), text: (x) => x.depositNumber },
    { label: t("depositDate"), cell: (x) => (x.depositDate ? <span className="num">{x.depositDate}</span> : <span className="muted">–</span>), text: (x) => x.depositDate ?? "" },
    { label: t("note"), cell: (x) => <span className="muted">{x.note}</span>, text: (x) => x.note },
    { label: t("by"), cell: (x) => x.byName ?? <span className="muted">–</span>, text: (x) => x.byName ?? "" }
  ];
  const title = t("cashTransfers");
  return (
    <Frame group="treasury" title={title} filters={<ReportFilters period={p} filters={[{ k: "fromTr", label: t("treasury"), options: f.tr }, { k: "toTr", label: t("toTreasury"), options: f.tr }, { k: "by", label: t("by"), options: f.users }]} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="transfers" cols={cols} rows={d.rows} foot={[total(t), blank, blank, blank, fcell(d.amount), blank, blank, blank, blank]}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

export function MovementSummaryReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/movement-summary", p, [["branchId", v.br], ["treasuryId", v.tr], ["userId", v.by]]), []);
  const { p, v, r, search } = useLoaded<MovementSummaryDto>(build);
  const d = r.data;
  const cols: RCol<MovementSummaryRow>[] = [
    { label: t("type"), cell: (x) => <>{srcLabel(t, x.source)} <small className="muted num">×{x.count}</small></>, text: (x) => `${srcLabel(t, x.source)} ×${x.count}` },
    { label: t("treasury"), cell: (x) => L(x.treasury), text: (x) => L(x.treasury) },
    { label: t("debit"), end: true, cell: (x) => <Amt v={x.debit} zero />, text: (x) => amt(x.debit) },
    { label: t("credit"), end: true, cell: (x) => <Amt v={x.credit} zero />, text: (x) => amt(x.credit) },
    { label: t("net"), end: true, cell: (x) => <Signed v={x.net} />, text: (x) => amt(x.net) }
  ];
  const title = t("movementSummary");
  return (
    <Frame group="treasury" title={title} filters={<ReportFilters period={p} filters={[{ k: "br", label: t("branch"), options: f.br }, { k: "tr", label: t("treasury"), options: f.tr }, { k: "by", label: t("by"), options: f.users }]} values={v} onSearch={search} />}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="movement-summary" cols={cols} rows={d.rows} foot={[total(t), blank, fcell(d.debit), fcell(d.credit), fcell(d.net)]}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

// ---------- warehouses (F-6) ----------

const WhNote = () => { const { t } = useLang(); return <div className="dep-note" data-testid="wh-note"><Icon name="info" /><span>{t("rpWhNote")}</span></div>; };

export function WarehouseBalancesReport() {
  const { t, L } = useLang();
  const build = useCallback(() => "/reports/warehouses", []);
  const { r } = useLoaded<WarehouseBalancesDto>(build, null);
  const d = r.data;
  const cols: RCol<WarehouseBalanceRow>[] = [
    { label: t("warehouse"), cell: (x) => <Link to={`/reports/warehouses-items?wh=${x.warehouse.id}`}>{L(x.warehouse)}</Link>, text: (x) => L(x.warehouse) },
    { label: t("branch"), cell: (x) => L(x.branch), text: (x) => L(x.branch) },
    { label: t("rpItemsHeld"), end: true, cell: (x) => <span className="num">{x.items}</span>, text: (x) => String(x.items) },
    { label: t("rpStockValue"), end: true, cell: (x) => <Amt v={x.value} />, text: (x) => amt(x.value) }
  ];
  const title = t("warehousesReport");
  return (
    <Frame group="warehouses" title={title} filters={<WhNote />}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="warehouses" cols={cols} rows={d.rows}
        foot={[total(t), blank, [String(d.items), String(d.items)], [<span data-testid="wh-total">{amt(d.value)}</span>, amt(d.value)]]} testId="report-table" />}</Body>
    </Frame>
  );
}

export function WarehouseItemsReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const wh = new URLSearchParams(window.location.search).get("wh") ?? "";
  const build = useCallback((_: Period | null, v: Record<string, string>) => qs("/reports/warehouse-items", null, [["warehouseId", v.wh], ["itemId", v.item], ["categoryId", v.cat], ["q", v.q?.trim()]]), []);
  const { v, r, search } = useLoaded<WarehouseItemsDto>(build, null, wh ? { wh } : {});
  const d = r.data;
  const cols: RCol<WarehouseItemRow>[] = [
    { label: t("branch"), cell: (x) => L(x.branch), text: (x) => L(x.branch) },
    { label: t("warehouse"), cell: (x) => L(x.warehouse), text: (x) => L(x.warehouse) },
    { label: t("itemName"), cell: (x) => <Link to={`/items/show/${x.item.id}`}>{L(x.item)}</Link>, text: (x) => L(x.item) },
    { label: t("categoryName"), cell: (x) => L(x.category), text: (x) => L(x.category) },
    { label: t("unit"), cell: (x) => L(x.unit), text: (x) => L(x.unit) },
    { label: t("qty"), end: true, cell: (x) => <span className="num">{x.qty}</span>, text: (x) => x.qty },
    { label: t("rpAvgCost"), end: true, cell: (x) => <span className="num">{x.average}</span>, text: (x) => x.average },
    { label: t("total"), end: true, cell: (x) => <Amt v={x.value} />, text: (x) => amt(x.value) }
  ];
  const title = t("whInventory");
  const filters: Filter[] = [{ k: "wh", label: t("warehouse"), options: f.wh }, { k: "item", label: t("itemName"), options: f.items, wide: true },
    { k: "q", label: t("searchWord"), options: [], text: t("rpSearchWordPh") }, { k: "cat", label: t("categoryName"), options: f.cats }];
  return (
    <Frame group="warehouses" title={title} filters={<><WhNote /><ReportFilters period={null} filters={filters} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="warehouses-inventory" cols={cols} rows={d.rows}
        foot={[total(t), blank, blank, blank, blank, blank, blank, [<span data-testid="whi-total">{amt(d.value)}</span>, amt(d.value)]]} testId="report-table" />}</Body>
    </Frame>
  );
}

// ---------- chart of accounts with balances (phase 9b) ----------

type CoaRow = { lv: 1 | 2 | 3; depth: number; code: string; name: string; amount: string; id?: string };
/** Every account under its class and groups, nested as deep as the chart goes, with balances as at the end date in their natural sign (F-1). */
export function ChartOfAccountsReport() {
  const { t, L } = useLang();
  const { opts } = useLedgerOptions();
  const build = useCallback((p: Period | null) => qs("/reports/chart-of-accounts", null, [["to", p?.to || undefined]]), []);
  const { p, r, search } = useLoaded<AccountMovementsDto>(build, "all");
  const d = r.data;
  const rows: CoaRow[] = [];
  if (d && opts) {
    const bal = new Map(d.rows.map((x) => [x.account.id, x.closing]));
    // Group figures from the server: each account beneath a group counted once at every level (rule H-6).
    const sub = new Map(d.groups.map((x) => [x.group.id, x.closing]));
    const nat = (l1: number, x: string) => (l1 === 1 || l1 === 4 ? Exact.dec(x) : Exact.sub(Exact.dec("0"), Exact.dec(x)));
    const fmt = (x: Exact.Dec) => Exact.fmt(x, Math.max(3, x.s));
    for (const g of opts.level1) {
      const top = opts.types.filter((x) => x.level1Id === g.id && !x.parentId);
      rows.push({ lv: 1, depth: 0, code: String(g.id), name: L(g), amount: fmt(Exact.sum(top.map((x) => nat(g.id, sub.get(x.id) ?? "0")))) });
      const walk = (ty: (typeof opts.types)[number], depth: number) => {
        rows.push({ lv: 2, depth, code: ty.code ?? "", name: L(ty), amount: fmt(nat(g.id, sub.get(ty.id) ?? "0")) });
        for (const c of opts.types.filter((x) => x.parentId === ty.id)) walk(c, depth + 1);
        for (const a of opts.accounts.filter((x) => x.typeId === ty.id)) rows.push({ lv: 3, depth: depth + 1, code: a.code, name: L(a), amount: fmt(nat(g.id, bal.get(a.id) ?? "0")), id: a.id });
      };
      for (const ty of top) walk(ty, 1);
    }
  }
  const cols: RCol<CoaRow>[] = [
    { label: t("name"), cell: (x) => <span className="rp-ind" style={{ paddingInlineStart: `${indentPx(x.depth, 18)}px` }}>{x.code && <span className="num muted">{x.code} </span>}{x.id ? <Link to={`/account/${x.id}`}>{x.name}</Link> : <b>{x.name}</b>}</span>,
      text: (x) => `${"  ".repeat(x.depth)}${x.code} ${x.name}`.trimEnd() },
    { label: t("amount"), end: true, cell: (x) => <Signed v={x.amount} />, text: (x) => amt(x.amount) }
  ];
  const title = t("chartOfAccounts");
  return (
    <Frame group="repAccounting" title={title} filters={<><Provisional /><ReportFilters period={p} filters={[]} values={{}} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="chart-of-accounts" cls="rp-tree" cols={cols} rows={rows} rowCls={(x) => "lv" + x.lv}
        chips={<span className="fchip">{t("rpAsAt")}: <b className="num">{d.to ?? t("pr_all")}</b></span>} testId="report-table" />}</Body>
    </Frame>
  );
}

// ---------- fixed assets (T-8) ----------

const AssetNote = () => { const { t } = useLang(); return <div className="dep-note" data-testid="rp-provisional"><Icon name="info" /><span>{t("assetProvisional")}</span></div>; };
/** Asset kinds for the filter; empty when the user may not read the asset screens. */
function useAssetKinds(): [string, string][] {
  const { L } = useLang();
  const [o, setO] = useState<AssetOptionsDto | null>(null);
  useEffect(() => { api<AssetOptionsDto>("GET", "/assets/options").then(setO, () => setO(null)); }, []);
  return (o?.kinds ?? []).map((k) => [k.id, L(k)]);
}

export function AssetsReport() {
  const { t, L } = useLang();
  const kinds = useAssetKinds();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/assets", p, [["kindId", v.kind]]), []);
  const { p, v, r, search } = useLoaded<AssetReportDto>(build);
  const d = r.data;
  const cols: RCol<AssetReportRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{x.at}</span>, text: (x) => x.at },
    { label: t("assetName"), cell: (x) => <>{x.title}{x.invoice && <span className="muted"> · <Link className="num" to={`/assets-purchase/show/${x.invoice.id}`}>{x.invoice.code}</Link></span>}</>, text: (x) => x.title + (x.invoice ? ` · ${x.invoice.code}` : "") },
    { label: t("fixedAssetKind"), cell: (x) => L(x.kind), text: (x) => L(x.kind) },
    { label: t("startWorkDate"), cell: (x) => <span className="num">{x.startOn}</span>, text: (x) => x.startOn },
    { label: t("fixedAssetAmount"), end: true, cell: (x) => <Amt v={x.amount} />, text: (x) => amt(x.amount) },
    { label: t("depreciationMonthly"), end: true, cell: (x) => <Amt v={x.monthly} zero />, text: (x) => amt(x.monthly) },
    { label: t("accumulatedDep"), end: true, cell: (x) => <Amt v={x.accumulated} zero />, text: (x) => amt(x.accumulated) },
    { label: t("bookValue"), end: true, cell: (x) => <Amt v={x.bookValue} />, text: (x) => amt(x.bookValue) }
  ];
  const title = t("allAssets");
  return (
    <Frame group="tabFixedAssets" title={title} filters={<><AssetNote /><ReportFilters period={p} filters={[{ k: "kind", label: t("fixedAssetKind"), options: kinds, wide: true }]} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <>
        <p className="hint" data-testid="rp-asset-asat">{t("assetRpAsAt")}</p>
        <ReportTable title={title} file="assets" cols={cols} rows={d.rows} foot={[total(t), blank, blank, blank, fcell(d.amount), fcell(d.monthly), fcell(d.accumulated), [<span data-testid="rp-book">{amt(d.bookValue)}</span>, amt(d.bookValue)]]}
          chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />
      </>}</Body>
    </Frame>
  );
}

export function AssetDepreciationReport() {
  const { t, L } = useLang();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/asset-depreciation", p, [["status", v.status]]), []);
  const { p, v, r, search } = useLoaded<DepreciationsDto>(build);
  const d = r.data;
  const cols: RCol<DepreciationRowDto>[] = [
    { label: t("assetTitle"), cell: (x) => x.asset.title, text: (x) => x.asset.title },
    { label: t("depAccount"), cell: (x) => <AccName a={x.accumulatedAccount} />, text: (x) => `${x.accumulatedAccount.code} ${L(x.accumulatedAccount)}` },
    { label: t("depExpAccount"), cell: (x) => <AccName a={x.expenseAccount} />, text: (x) => `${x.expenseAccount.code} ${L(x.expenseAccount)}` },
    { label: t("fromF"), cell: (x) => <span className="num">{x.fromOn}</span>, text: (x) => x.fromOn },
    { label: t("toF"), cell: (x) => <span className="num">{x.toOn}</span>, text: (x) => x.toOn },
    { label: t("isDeported"), cell: (x) => (x.entry ? <Link className="num" to={`/journals/show/${x.entry.id}`}>{x.entry.number}</Link> : <span className="muted">{t("deportedNo")}</span>), text: (x) => x.entry?.number ?? t("deportedNo") },
    { label: t("amount"), end: true, cell: (x) => <Amt v={x.amount} />, text: (x) => amt(x.amount) }
  ];
  const title = t("assetDepreciation");
  const filters: Filter[] = [{ k: "status", label: t("isDeported"), options: [["deported", t("deportedYes")], ["open", t("deportedNo")]] }];
  return (
    <Frame group="tabFixedAssets" title={title} filters={<><AssetNote /><ReportFilters period={p} filters={filters} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="asset-depreciation" cols={cols} rows={d.rows}
        foot={[total(t), blank, blank, blank, blank, [<span>{t("deportedTotal")}: <span className="num" data-testid="rp-dep-deported">{amt(d.deported)}</span></span>, amt(d.deported)], [<span data-testid="rp-dep-total">{amt(d.total)}</span>, amt(d.total)]]}
        chips={<PeriodChip from={p?.from || null} to={p?.to || null} />} testId="report-table" />}</Body>
    </Frame>
  );
}

function AssetInvoiceTable({ title, file, d, withPaid }: { title: string; file: string; d: AssetInvoiceReportDto; withPaid: boolean }) {
  const { t, L } = useLang();
  const cols: RCol<AssetInvoiceReportRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("warehouse"), cell: (x) => L(x.warehouse), text: (x) => L(x.warehouse) },
    { label: t("account"), cell: (x) => partyName(t, x.party), text: (x) => partyName(t, x.party) },
    { label: t("invoiceNo"), cell: (x) => <DocNo to={`/assets-purchase/show/${x.id}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("subTotal"), end: true, cell: (x) => <Signed v={x.subtotal} />, text: (x) => amt(x.subtotal) },
    { label: t("tax"), end: true, cell: (x) => <Signed v={x.vat} />, text: (x) => amt(x.vat) },
    { label: t("grandTotal"), end: true, cell: (x) => <Signed v={x.grand} />, text: (x) => amt(x.grand) },
    ...(withPaid ? [
      { label: t("paidTotal"), end: true, cell: (x: AssetInvoiceReportRow) => <Signed v={x.paid} />, text: (x: AssetInvoiceReportRow) => amt(x.paid) },
      { label: t("remaining"), end: true, cell: (x: AssetInvoiceReportRow) => <Signed v={x.remaining} />, text: (x: AssetInvoiceReportRow) => amt(x.remaining) }] : []),
    { label: t("by"), cell: (x) => x.byName ?? <span className="muted">–</span>, text: (x) => x.byName ?? "" }
  ];
  const foot: [ReactNode, string][] = [total(t), blank, blank, blank, fcell(d.subtotal), fcell(d.vat), fcell(d.grand), ...(withPaid ? [fcell(d.paid), fcell(d.remaining)] : []), blank];
  return <ReportTable title={title} file={file} cols={cols} rows={d.rows} foot={foot} chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />;
}

function assetInvoiceFilters(t: T, f: ReturnType<typeof useFilterOpts>): Filter[] {
  return [{ k: "wh", label: t("warehouse"), options: f.wh }, { k: "acc", label: t("account"), options: f.party("supplier"), wide: true }, { k: "by", label: t("by"), options: f.users }];
}

/** Asset purchases (r9purchase), and the asset tax report (r7asset): the same purchases without what was paid. */
export function AssetInvoicesReport({ tax }: { tax?: boolean }) {
  const { t } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs(tax ? "/reports/asset-tax" : "/reports/asset-invoices", p, [["warehouseId", v.wh], ["partyId", v.acc], ["userId", v.by]]), [tax]);
  const { p, v, r, search } = useLoaded<AssetInvoiceReportDto>(build);
  const title = t(tax ? "assetTax" : "assetPurchases");
  return (
    <Frame group={tax ? "repTaxes" : "tabFixedAssets"} title={title} filters={<><AssetNote /><ReportFilters key={String(tax)} period={p} filters={assetInvoiceFilters(t, f)} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{r.data && <AssetInvoiceTable title={title} file={tax ? "asset-tax" : "asset-purchases"} d={r.data} withPaid={!tax} />}</Body>
    </Frame>
  );
}

export function AssetInvoiceDetailsReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const build = useCallback((p: Period | null, v: Record<string, string>) => qs("/reports/asset-invoice-details", p, [["warehouseId", v.wh], ["partyId", v.acc], ["userId", v.by]]), []);
  const { p, v, r, search } = useLoaded<AssetInvoiceDetailsDto>(build);
  const d = r.data;
  const cols: RCol<AssetInvoiceDetailRow>[] = [
    { label: t("date"), cell: (x) => <span className="num">{when(x.at)}</span>, text: (x) => when(x.at) },
    { label: t("warehouse"), cell: (x) => L(x.warehouse), text: (x) => L(x.warehouse) },
    { label: t("invoiceNo"), cell: (x) => <DocNo to={`/assets-purchase/show/${x.id}`} code={x.code} reversal={x.reversal} />, text: (x) => docText(t, x.code, x.reversal) },
    { label: t("statement"), cell: (x) => x.statement, text: (x) => x.statement },
    { label: t("fixedAssetKind"), cell: (x) => L(x.kind), text: (x) => L(x.kind) },
    { label: t("qty"), end: true, cell: (x) => <span className="num">{x.qty.replace(/\.?0+$/, "")}</span>, text: (x) => x.qty },
    { label: t("price"), end: true, cell: (x) => <Amt v={x.price} />, text: (x) => amt(x.price) },
    { label: t("tax"), end: true, cell: (x) => <Signed v={x.tax} />, text: (x) => amt(x.tax) },
    { label: t("total"), end: true, cell: (x) => <Signed v={x.total} />, text: (x) => amt(x.total) },
    { label: t("by"), cell: (x) => x.byName ?? <span className="muted">–</span>, text: (x) => x.byName ?? "" }
  ];
  const title = t("assetInvoiceDetails");
  return (
    <Frame group="tabFixedAssets" title={title} filters={<><AssetNote /><ReportFilters period={p} filters={assetInvoiceFilters(t, f)} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <ReportTable title={title} file="asset-invoice-details" cols={cols} rows={d.rows}
        foot={[total(t), blank, blank, blank, blank, blank, blank, fcell(d.tax), [<span data-testid="rp-details-total">{amt(d.total)}</span>, amt(d.total)], blank]}
        chips={<PeriodChip from={d.from} to={d.to} />} testId="report-table" />}</Body>
    </Frame>
  );
}

// ---------- item movement (correction pass 2026-10-27, item 5; docs/item-movement.md) ----------

type ImLine = { open: string } | { row: ItemMovementRow };
/**
 * Every posted movement of one item in the caller's warehouses: the opening (all earlier movements), each document
 * in and out with its warehouse and running balance, and the closing, reconciled to the stock held now.
 */
export function ItemMovementReport() {
  const { t, L } = useLang();
  const f = useFilterOpts();
  const init = new URLSearchParams(window.location.search);
  const initial: Record<string, string> = {};
  for (const k of ["item", "wh", "br"]) if (init.get(k)) initial[k] = init.get(k)!;
  const build = useCallback((p: Period | null, v: Record<string, string>) => v.item ? qs("/reports/item-movement", p, [["itemId", v.item], ["warehouseId", v.wh], ["branchId", v.br]]) : null, []);
  const { p, v, r, search } = useLoaded<ItemMovementDto>(build, "thisYear", initial);
  const d = r.data;
  const q3 = (x: string) => <span className="num">{x}</span>;
  const rows: ImLine[] = d ? [{ open: d.opening }, ...d.rows.map((row) => ({ row }))] : [];
  const cols: RCol<ImLine>[] = [
    { label: t("date"), cell: (x) => ("row" in x ? <span className="num">{when(x.row.at)}</span> : <b>{t("imOpening")}</b>), text: (x) => ("row" in x ? when(x.row.at) : t("imOpening")) },
    { label: t("stDocument"), cell: (x) => ("row" in x ? <span data-testid="im-doc"><DocNo to={sourcePath(x.row.source)} code={x.row.source.code} reversal={x.row.source.reversal} /></span> : ""),
      text: (x) => ("row" in x ? docText(t, x.row.source.code, x.row.source.reversal) : "") },
    { label: t("imType"), cell: (x) => ("row" in x ? t(`imType_${x.row.type}`) : ""), text: (x) => ("row" in x ? t(`imType_${x.row.type}`) : "") },
    { label: t("warehouse"), cell: (x) => ("row" in x ? L(x.row.warehouse) : ""), text: (x) => ("row" in x ? L(x.row.warehouse) : "") },
    { label: t("unit"), cell: () => (d ? L(d.unit) : ""), text: () => (d ? L(d.unit) : "") },
    { label: t("imIn"), end: true, cell: (x) => ("row" in x ? q3(x.row.qtyIn) : ""), text: (x) => ("row" in x ? x.row.qtyIn : "") },
    { label: t("imOut"), end: true, cell: (x) => ("row" in x ? q3(x.row.qtyOut) : ""), text: (x) => ("row" in x ? x.row.qtyOut : "") },
    { label: t("imBalance"), end: true, cell: (x) => <b className="num" data-testid="im-balance">{"row" in x ? x.row.balance : x.open}</b>, text: (x) => ("row" in x ? x.row.balance : x.open) }
  ];
  const ok = d && isZero(d.difference);
  const notes = d ? [
    ok ? t("imReconciled", { x: d.onHand }) : t("imNotReconciled", { l: d.ledger, h: d.onHand, d: d.difference }),
    ...d.byType.map((b) => `${t(`imType_${b.type}`)}: ${t("imIn")} ${b.qtyIn} · ${t("imOut")} ${b.qtyOut}`),
    t("imDraftsNote")
  ] : [];
  const title = t("itemMovement");
  const filters: Filter[] = [{ k: "item", label: t("itemName"), options: f.items, wide: true, required: true }, { k: "br", label: t("rpBranch"), options: f.br }, { k: "wh", label: t("warehouse"), options: f.wh }];
  return (
    <Frame group="warehouses" title={title} filters={<><WhNote /><ReportFilters period={p} filters={filters} values={v} onSearch={search} /></>}>
      <Body failed={r.failed} loading={r.loading} need={v.item ? undefined : t("itemName")}>{d && <>
        <div className={`banner ${ok ? "good" : "bad"} rp-check`} data-testid="im-check"><Icon name={ok ? "check" : "alert"} /><span>{notes[0]}</span></div>
        <ReportTable title={`${d.item.code} ${L(d.item)}`} file="item-movement" cols={cols} rows={rows} notes={notes} rowCls={(x) => ("open" in x ? "rp-s" : "")}
          foot={[[t("imClosing"), t("imClosing")], blank, blank, blank, blank, [q3(d.totalIn), d.totalIn], [q3(d.totalOut), d.totalOut], [<b className="num" data-testid="im-closing">{d.closing}</b>, d.closing]]}
          chips={<><PeriodChip from={d.from} to={d.to} /><span className="fchip">{t("itemName")}: <b>{d.item.code} {L(d.item)}</b></span>
            {d.branch && <span className="fchip">{t("rpBranch")}: <b>{L(d.branch)}</b></span>}{d.warehouse && <span className="fchip">{t("warehouse")}: <b>{L(d.warehouse)}</b></span>}</>} testId="report-table" />
      </>}</Body>
    </Frame>
  );
}
